// Project-import presentation helpers.
//
// This module is intentionally pure: it owns the human-facing intake model
// (the RJ Bird 01 + 02 package convention, queue identity, and the five clear
// workflow states) without reading or writing Dexie. FileExplorer and
// QueueDrawer can therefore share the same vocabulary while the queue engine
// remains independently testable.

import {
    extractQuoteReference,
    resolveProjectQuoteReference,
} from '../../services/project-reference';
import { buildQueueDisplayMeta } from '../../services/project-intake-persistence.js';
import { hasDurableIntakeReadyProof } from '../../services/project-intake-readiness.js';
import { matchProjectSubfolder } from '../../config/folder-conventions.js';
import { isProjectDeletionTombstone } from '../../services/project-deletion-tombstone.js';
import { modelCapabilityMessage, type ModelCapabilityGap } from '../../services/ai-error-handling';

export { extractQuoteReference };

export const REQUIRED_INTAKE_PACKAGES = [
    { key: 'rfq', role: 'rfq', folderName: '01. RFQ Package', label: 'RFQ package' },
    { key: 'site', role: 'site-docs', folderName: '02. Site Documentation Package', label: 'Site documentation' },
] as const;

/**
 * A curated selection key is one of three shapes: a scan/materialised
 * namespaced id (`<projectScope>::<relPath>` — the primary staging path
 * preserves this end-to-end), a bare relPath (an arrival Included without a
 * resolvable durable id), or an opaque Dexie id (`doc_…` — legacy flows; no
 * path can be recovered). Display surfaces and pack-membership checks work
 * on the relPath, so this is THE one place that extracts it.
 */
export function relPathFromSelectionKey(key: any): string {
    const raw = String(key ?? '');
    const separator = raw.indexOf('::');
    if (separator >= 0) return raw.slice(separator + 2);
    return raw.includes('/') ? raw : '';
}

export const IMPORT_WORKFLOW_TONE = {
    ready: {
        label: 'Ready',
        description: 'Ready to add to the preparation queue',
        color: '#047857',
        bg: '#ecfdf5',
        border: '#10b981',
    },
    // S2 (2026-08-19, docs/import-staging/SPEC.md §3): staging states.
    staged: {
        label: 'Staged',
        description: 'Files curated. Not queued yet',
        color: '#334155',
        bg: '#f8fafc',
        border: '#94a3b8',
    },
    waiting_files: {
        label: 'Waiting for files',
        description: 'Staged while reference files are still arriving',
        color: '#92400e',
        bg: '#fffbeb',
        border: '#f59e0b',
    },
    queued: {
        label: 'Queued',
        description: 'Waiting for the operator to start preparation',
        color: '#374151',
        bg: '#f3f4f6',
        border: '#9ca3af',
    },
    processing: {
        label: 'Processing',
        description: 'QuoteMaker is actively preparing this project',
        color: 'var(--qm-accent-700)',
        bg: 'var(--qm-accent-50)',
        border: 'var(--qm-accent)',
    },
    needs_review: {
        label: 'Needs review',
        description: 'Draft is ready for a human to fine-tune in Stages 4 and 5',
        color: '#6d28d9',
        bg: '#f5f3ff',
        border: '#8b5cf6',
    },
    failed: {
        label: 'Failed',
        description: 'Preparation stopped and needs attention',
        color: '#b91c1c',
        bg: '#fef2f2',
        border: '#dc2626',
    },
} as const;

export type ImportWorkflowState = keyof typeof IMPORT_WORKFLOW_TONE;

/**
 * Failures about the business or the AI service, not the job: every job after
 * the first would stop the same way (Quote Run SPEC §4.2). The Quote Run
 * controls end the run on these, and the busy ones reach a failed row only
 * after the engine's own waits have run out.
 *
 * The engine keeps any code it is handed (batch-queue.js _normaliseBatchError
 * classifies only an empty one), so the AI gateway's own codes reach a row
 * as they are: its budget and allow-list stops (ai-gateway.js policyBlocked),
 * its provider refusals `upstream_<status>` (upstreamError), its no-endpoint
 * block, and its timeouts and broken answers once the waits have run out.
 * The App stage hooks add AI_KEY_MISSING, and a business with no price list
 * throws QM_PRICING_DATA_UNAVAILABLE (pricing.js) at Stage 3 for every job.
 * A model chosen for a task that cannot take its request (the gateway's
 * model_no_* codes, backend openrouter-no-endpoints.js) refuses every job's
 * call to that task the same way.
 */
export const RUN_WIDE_FAILURE_CODES: readonly string[] = Object.freeze([
    'AI_CREDIT_EXHAUSTED',
    'AI_AUTH_FAILED',
    'AI_PROVIDER_NOT_CONFIGURED',
    'AI_NO_MATCHING_ENDPOINT',
    'ALL_KEYS_QUARANTINED',
    'AI_RATE_LIMITED',
    'UPSTREAM_429',
    // The model's PROVIDER refused (ai-gateway.js providerRateLimitedError):
    // every job's call to that model meets the same limit.
    'UPSTREAM_PROVIDER_RATE_LIMITED',
    'NETWORK_FAILED',
    'DAILY_LIMIT_REACHED',
    'HARD_LIMIT_REACHED',
    'MODEL_NOT_ALLOWED',
    'FEATURE_NOT_ALLOWED',
    'UPSTREAM_401',
    'UPSTREAM_402',
    'UPSTREAM_403',
    'NO_MATCHING_ENDPOINTS',
    'MODEL_NO_TOOL_SUPPORT',
    'MODEL_NO_IMAGE_SUPPORT',
    'MODEL_NO_PARAMETER_SUPPORT',
    'MODEL_NO_ENDPOINTS',
    'AI_KEY_MISSING',
    'UPSTREAM_TIMEOUT',
    'UPSTREAM_INVALID_JSON',
    'UPSTREAM_STREAM_ERROR',
    'QM_PRICING_DATA_UNAVAILABLE',
]);

// `upstream_${status}` for any 5xx the provider answers (ai-gateway.js
// upstreamError): the service was down, waited on first like every busy code.
const UPSTREAM_SERVER_ERROR = /^UPSTREAM_5\d\d$/;

export function isRunWideFailureCode(code: unknown): boolean {
    if (typeof code !== 'string' || !code.trim()) return false;
    const key = code.trim().toUpperCase();
    return RUN_WIDE_FAILURE_CODES.includes(key) || UPSTREAM_SERVER_ERROR.test(key);
}

/**
 * One operator-retry policy for every failed-job surface.
 *
 * `lastErrorRetryable=false` is a terminal result for deterministic input or
 * data failures, so presenting Retry would only repeat the same work. Provider
 * and model configuration failures are a deliberate exception: an operator
 * can fix the key, model lane, or OpenRouter policy outside the job and then
 * retry the unchanged project successfully. PROJECT_NOT_LOCAL has a separate
 * opt-in exception only for Import Projects after a valid Folder 01+02 rescan.
 */
export function canRetryFailedQueueJob(
    job: any,
    { sourceRefreshAvailable = false }: { sourceRefreshAvailable?: boolean } = {},
): boolean {
    if (!job || job.status !== 'failed' || job.id == null) return false;
    if (isProjectDeletionTombstone(job)) return false;
    if (job.lastErrorRetryable !== false) return true;

    // PROJECT_STALE_WRITE is non-retryable at the SAVE level (re-sending the
    // same payload would be refused again) but retryable at the JOB level:
    // a queue retry re-loads the project from the server first, which adopts
    // the newest copy AND its concurrency token, so the stale precondition
    // cannot recur. Without this exception the failed row offered NO recovery
    // at all — the operator dead-ended unless they opened the project and
    // found the reload banner. Proven live 2026-08-19: the one stale-write
    // batch failure completed cleanly once retried with a fresh load.
    if (job.lastErrorCode === 'PROJECT_STALE_WRITE') return true;

    // A run-wide stop the engine marked final (batch-queue.js
    // _normaliseBatchError nonRetryableCodes, or the gateway's own
    // retryable:false on a provider refusal) is final for the job as it
    // stood, but the fix is outside the job: top up the credit, sign in
    // again, wait for the budget, or have support set the AI service up.
    // After that the same job runs, so a failed row must still offer Try
    // again (SPEC §7).
    if (isRunWideFailureCode(job.lastErrorCode)) return true;

    // PROJECT_NOT_LOCAL is terminal to the generic queue because retrying its
    // stale snapshot cannot recreate a deleted Dexie row. Import Projects is
    // the one safe exception: after a fresh scan proves Folder 01 + 02 are
    // available, its retry path re-materialises those handles and performs an
    // atomic source refresh. Callers must opt in only on that surface; the
    // Queue drawer deliberately leaves this false.
    if (sourceRefreshAvailable
        && (job.phase || 'prepare-quote') === 'pre-processing'
        && job.lastErrorCode === 'PROJECT_NOT_LOCAL') {
        return true;
    }

    const evidence = [
        job.lastErrorCode,
        job.lastErrorCategory,
        job.lastError,
        job.lastErrorDetails?.category,
        job.lastErrorDetails?.operatorAction,
    ].filter(Boolean).join(' ').toLowerCase();

    return /ai[_ -]?provider|openrouter|data[_ -]?policy|privacy|model.*(?:allowed|available|configured|configuration)/
        .test(evidence);
}

export type FailedQueueRecoveryKind =
    | 'restore-permission'
    | 'retry-saved-intake'
    | 'review-source'
    | 'continue-quote'
    | 'unavailable';

export type BuilderDecisionOpenAction = {
    stage: 1;
    label: 'Open Stage 1';
};

export type QuoteRunCoordinationOutcome = {
    kind: 'busy' | 'stale' | 'ownership';
    title:
        | 'Running in another session'
        | 'Held by a closed page'
        | 'Free to try again'
        | 'Updated elsewhere'
        | 'Stopped for review'
        | 'Reviewed, ready to try again'
        | 'Reviewed, start it again';
    description: string;
    /** The button's label: the outcome's own, or `step.label` for 'next-step'. */
    actionLabel: string;
    /** 'next-step': Try again would be refused, so the button is `step`. */
    action: 'retry' | 'review' | 'next-step';
    tone: 'slate' | 'amber';
    /**
     * Only when the engine marked the stop final (canRetryFailedQueueJob is
     * false): the step the job offers instead of Try again, after any review.
     */
    step?: FailedJobStep;
    /**
     * A busy answer that was this browser's own closed page (closedPageClaimHold):
     * when its claim lets go, and whether it still holds the job at `now`.
     */
    hold?: { freesAt: number; held: boolean };
};

/**
 * How long a Quote Run's server claim and its local job lease last: the
 * engine's QUOTE_RUN_CLAIM_TTL_SECONDS and JOB_LEASE_DURATION_MS
 * (batch-queue.js). Every heartbeat renews both, the lease first.
 */
export const QUOTE_RUN_CLAIM_TTL_MS = 180_000;
export const QUOTE_RUN_JOB_LEASE_MS = 90_000;
// The network and the two clocks: the server's lease_expires_at against this
// browser's interruptedAt.
const CLOSED_PAGE_HOLD_SLACK_MS = 30_000;

/**
 * Was a busy answer this browser's own closed page, not another session?
 *
 * A page that closes (a reload, a crash) keeps its server claim until the
 * claim's lease runs out, and nothing releases it. The next attempt claims
 * with a new claim id, which the server refuses as PROJECT_QUOTE_RUN_BUSY
 * while the old claim lasts (projects.js quote-run-claim: the same owner, a
 * finished claim or an expired lease only). The job row still carries the
 * closed page's claim id, never released, and the time its lease reset
 * (interruptedAt, batch-queue.js hydrate and recoverExpiredQueueLeases).
 *
 * The reset comes after the local lease ended, so the closed page's last
 * renewal was at least QUOTE_RUN_JOB_LEASE_MS before it, and its claim ends at
 * most QUOTE_RUN_CLAIM_TTL_MS after that renewal. A claim that lasts longer is
 * being renewed by a live session. The refusal's own end time
 * (lastErrorDetails.claimFreesAt, the 409's lease_expires_at) tells them
 * apart; without it nothing is claimed.
 */
export function closedPageClaimHold(job: any): { freesAt: number } | null {
    if (String(job?.lastErrorCode || '').trim().toUpperCase() !== 'PROJECT_QUOTE_RUN_BUSY') return null;
    if (!job?.quoteRunClaimId || job?.quoteRunClaimReleasedAt) return null;
    const interrupted = Date.parse(String(job?.interruptedAt || ''));
    const freesAt = Date.parse(String(job?.lastErrorDetails?.claimFreesAt || ''));
    if (!Number.isFinite(interrupted) || !Number.isFinite(freesAt)) return null;
    // A refusal from before the reset is not this attempt's.
    const refusedAt = Date.parse(String(job?.completedAt || ''));
    if (Number.isFinite(refusedAt) && refusedAt < interrupted) return null;
    const latestEnd = interrupted + (QUOTE_RUN_CLAIM_TTL_MS - QUOTE_RUN_JOB_LEASE_MS) + CLOSED_PAGE_HOLD_SLACK_MS;
    return freesAt <= latestEnd ? { freesAt } : null;
}

const QUOTE_RUN_REVIEW_CODES = new Set([
    'PROJECT_QUOTE_RUN_OWNER_MISMATCH',
    'PROJECT_QUOTE_RUN_CLAIM_NOT_ACTIVE',
    'PROJECT_QUOTE_RUN_CLAIM_NOT_FOUND',
    'PROJECT_QUOTE_RUN_CLAIM_UNCERTAIN',
    'PROJECT_QUOTE_RUN_RECEIPT_INVALID',
    'PROJECT_QUOTE_RUN_VERSION_MISSING',
    'PROJECT_NOT_FOUND',
]);

/**
 * Claim and stale-write outcomes are coordination, not broken quote work.
 * Keep their copy and recovery verb identical wherever Import Projects renders
 * them so a second browser never looks like an operator-caused failure.
 */
/**
 * Has the operator looked at THIS stop? `acknowledgeCoordinationReview` pins
 * its acknowledgement to the job's terminal stamp, so a later failure is not
 * covered by an earlier review.
 */
export function coordinationReviewIsCurrent(job: any): boolean {
    const reviewedFor = job?.coordinationReviewedFor ?? null;
    if (!job?.coordinationReviewedAt || reviewedFor === null) return false;
    return reviewedFor === (job?.completedAt || job?.updatedAt || null);
}

/**
 * The step a coordination stop offers when Try again would be refused: the
 * engine marked it final (lastErrorRetryable false, as an empty or mismatched
 * claim receipt is, pipeline-runner.js quoteRunReceiptError), and the retry
 * path refuses what canRetryFailedQueueJob refuses (FileExplorer
 * recoverFailedQueueJob). Null while Try again works. A job handed in without
 * a status or id (an action refusal) is read as a row, as nextStepSentence does.
 */
function coordinationFinalStep(job: any): FailedJobStep | null {
    const row = { ...job, status: 'failed', id: job?.id ?? 0 };
    return canRetryFailedQueueJob(row) ? null : failedJobNextStep(row);
}

export function quoteRunCoordinationOutcome(job: any, now: number = Date.now()): QuoteRunCoordinationOutcome | null {
    const code = String(job?.lastErrorCode || '').toUpperCase();
    if (code === 'PROJECT_QUOTE_RUN_BUSY') {
        const finalStep = coordinationFinalStep(job);
        // This browser's own page that closed, not another session: say so,
        // and when it lets go (quote-run-view redraws the seconds).
        const closedPage = closedPageClaimHold(job);
        if (closedPage) {
            const seconds = Math.max(0, Math.ceil((closedPage.freesAt - now) / 1000));
            const held = seconds > 0;
            const what = held
                ? `The page that closed still holds this job for ${seconds} s, so QuoteMaker left this project unchanged.`
                : 'The page that closed was still holding this job when the run reached it. That hold has ended.';
            const title = held ? 'Held by a closed page' : 'Free to try again';
            const hold = { freesAt: closedPage.freesAt, held };
            if (finalStep) {
                return {
                    kind: 'busy',
                    title,
                    description: held ? `${what} After that, ${nextStepSentence(job)}` : `${what} ${nextStepSentence(job)}`,
                    actionLabel: finalStep.label,
                    action: 'next-step',
                    tone: 'slate',
                    step: finalStep,
                    hold,
                };
            }
            return {
                kind: 'busy',
                title,
                description: what,
                actionLabel: 'Try when free',
                action: 'retry',
                tone: 'slate',
                hold,
            };
        }
        if (finalStep) {
            return {
                kind: 'busy',
                title: 'Running in another session',
                description: `QuoteMaker left this project unchanged. Once that session has finished, ${nextStepSentence(job)}`,
                actionLabel: finalStep.label,
                action: 'next-step',
                tone: 'slate',
                step: finalStep,
            };
        }
        return {
            kind: 'busy',
            title: 'Running in another session',
            description: 'QuoteMaker left this project unchanged.',
            actionLabel: 'Try when free',
            action: 'retry',
            tone: 'slate',
        };
    }
    if (code === 'PROJECT_STALE_WRITE') {
        return {
            kind: 'stale',
            title: 'Updated elsewhere',
            description: 'Review the latest saved project before trying the run again.',
            actionLabel: 'Review latest project',
            action: 'review',
            tone: 'amber',
        };
    }
    if (QUOTE_RUN_REVIEW_CODES.has(code)) {
        // The second half of the sentence this row has always shown. Until the
        // operator has looked, the action is the review; once they have, it is
        // the retry the copy promised. Pinned to THIS failure, so a later stop
        // asks to be looked at again instead of inheriting an old glance.
        // A stop the engine marked final is refused by Try again, so its words
        // and, after the look, its button are the step the job does have.
        // The words say this job stopped, never that the run paused: a claim
        // stop fails the one job (only pauseQueue pauses the engine), and a
        // Quote Run carries on with the next job (skipOnFailure).
        const finalStep = coordinationFinalStep(job);
        if (finalStep) {
            if (coordinationReviewIsCurrent(job)) {
                return {
                    kind: 'ownership',
                    title: 'Reviewed, start it again',
                    description: 'You have seen the latest saved project. QuoteMaker cannot carry this job on from where it stopped. '
                        + nextStepSentence(job),
                    actionLabel: finalStep.label,
                    action: 'next-step',
                    tone: 'amber',
                    step: finalStep,
                };
            }
            return {
                kind: 'ownership',
                title: 'Stopped for review',
                description: 'QuoteMaker stopped this job before its next stage. Completed work is retained. '
                    + `Review the latest project. After that, ${nextStepSentence(job)}`,
                actionLabel: 'Review latest project',
                action: 'review',
                tone: 'amber',
                step: finalStep,
            };
        }
        if (coordinationReviewIsCurrent(job)) {
            return {
                kind: 'ownership',
                title: 'Reviewed, ready to try again',
                description: 'You have seen the latest saved project. Completed work is retained and the run picks up from there.',
                actionLabel: 'Try again',
                action: 'retry',
                tone: 'amber',
            };
        }
        return {
            kind: 'ownership',
            title: 'Stopped for review',
            description: 'QuoteMaker stopped this job before its next stage. Completed work is retained. Review the latest project, then try again.',
            actionLabel: 'Review before retry',
            action: 'review',
            tone: 'amber',
        };
    }
    return null;
}

/**
 * A deterministic builder decision is not a retry. The unattended runner has
 * already stopped before changing anything, so the next honest action is to
 * open the attended stage that owns the decision.
 */
export function builderDecisionOpenAction(job: any): BuilderDecisionOpenAction | null {
    const code = String(job?.lastErrorCode || '').toUpperCase();
    if (code === 'RFQ_PREFLIGHT_BLOCKED') {
        return { stage: 1, label: 'Open Stage 1' };
    }
    return null;
}

const SOURCE_REVIEW_REQUIRED_CODES = new Set([
    'SOURCE_CHANGED',
    'FILE_MATERIALISATION_INCOMPLETE',
    'FILE_PREPROCESS_BUILD_INCOMPLETE',
    'PROJECT_NOT_LOCAL',
]);

function hasReconnectableDeferredSource(job: any): boolean {
    const deferred = job?.deferredIntake;
    if (!deferred) return false;
    if (deferred.directoryHandle) return true;
    return Array.isArray(deferred.fileRecords)
        && deferred.fileRecords.some((record: any) => !!record?.handle);
}

/**
 * The one recovery contract for a failed queue job.
 *
 * Intake failures are not quote-stage retries. Before materialisation is
 * complete, the saved queue snapshot cannot be trusted to represent the
 * current folder, so the operator must review a fresh scan. The only safe
 * exception is a retained directory handle that merely needs permission.
 */
export function classifyFailedQueueRecovery(job: any): FailedQueueRecoveryKind {
    if (!job || job.status !== 'failed' || job.id == null) return 'unavailable';
    if (isProjectDeletionTombstone(job)) return 'unavailable';

    const phase = job.phase || 'prepare-quote';
    if (phase !== 'pre-processing') {
        return canRetryFailedQueueJob(job) ? 'continue-quote' : 'unavailable';
    }

    const code = String(job.lastErrorCode || '').toUpperCase();
    if (code === 'SOURCE_PERMISSION_REQUIRED' && hasReconnectableDeferredSource(job)) {
        return 'restore-permission';
    }
    if (SOURCE_REVIEW_REQUIRED_CODES.has(code)) return 'review-source';
    if (job.materializationState === 'complete' && canRetryFailedQueueJob(job)) {
        // E22 (G14): a saved-intake retry does not rebuild the extracted
        // text, so once a retry has stopped the same way the way on is a
        // fresh read of the folder. Browse, the Queue drawer and the job
        // panel all act on this kind, so none of them retries the same saved
        // intake a third time.
        return isRepeatedFailure(job) ? 'review-source' : 'retry-saved-intake';
    }

    // Any other pre-materialisation failure must go back through the current
    // folder scan. A generic retry could otherwise replay stale or partial
    // source evidence even when the engine marked the original error retryable.
    return 'review-source';
}

// ─── A stopped job's next step ──────────────────────────────────────────────
// The Quote Run row (quote-run-view failedRowAction), its detail panel
// (run-blocker retryPlan, RunCard's Other choices) and the failure words
// below all read these, so the words, the row's one button and the panel's
// other choices cannot offer different paths for one job.

/**
 * Stops whose fix is a fresh pick of the job's files. Carrying on, or
 * starting over from the old picks, stops at the same check before any AI
 * runs: batch-queue.js preflight (PROJECT_NOT_LOCAL) and pipeline-runner.js
 * (ANALYSIS_INPUT_EMPTY, SELECTED_FILE_SCOPE_EMPTY).
 */
// DOCUMENT_NOT_STORED: the file service refused one of the picked files, and
// the same picks send it again (project-intake-persistence.js backendSyncError).
const FRESH_PICKS_CODES = new Set(['PROJECT_NOT_LOCAL', 'ANALYSIS_INPUT_EMPTY', 'SELECTED_FILE_SCOPE_EMPTY', 'DOCUMENT_NOT_STORED']);

export function failedJobNeedsFreshPicks(job: any): boolean {
    return FRESH_PICKS_CODES.has(String(job?.lastErrorCode || '').trim().toUpperCase());
}

/** May a stopped job start over from its own saved file picks? */
export function canStartOverFailedJob(job: any): boolean {
    const phase = job?.phase || 'prepare-quote';
    const picks = Array.isArray(job?.selectedFileIds) && job.selectedFileIds.length > 0;
    return phase === 'prepare-quote' && picks && !failedJobNeedsFreshPicks(job);
}

/** May a stopped job carry on from where it stopped (Try again)? */
export function canCarryOnFailedJob(job: any): boolean {
    return classifyFailedQueueRecovery(job) === 'continue-quote' && !failedJobNeedsFreshPicks(job);
}

export type FailedJobStep =
    | { kind: 'restore-permission' | 'review-source' | 'retry' | 'repick-files' | 'start-over'; label: string }
    /** Opens the job: `why` says why its files are not read again. */
    | { kind: 'open-stage'; label: 'Open the job'; why: string }
    /** The next step is outside Quote Run; `why` says where. */
    | { kind: 'elsewhere'; label: ''; why: string };

/**
 * The one button a stopped job offers before any retry of it has failed the
 * same way. A project that already has newer quote work in the cloud is
 * opened, not read again (project-intake-persistence.js
 * backendNewerThanIntake). A quote request that belongs to Intake is made
 * there (intake-project-handoff.js): reading the folder again meets the same
 * refusal, so no Quote Run button is offered for it.
 */
export function failedJobNextStep(job: any): FailedJobStep {
    const code = String(job?.lastErrorCode || '').trim().toUpperCase();
    if (code === 'INTAKE_HANDOFF_REQUIRED') {
        return { kind: 'elsewhere', label: '', why: 'Finish it in Intake, from the menu.' };
    }
    if (code === 'PROJECT_BACKEND_NEWER_THAN_LOCAL') {
        return {
            kind: 'open-stage',
            label: 'Open the job',
            why: 'Reading its files again would not replace the quote work saved in the cloud.',
        };
    }
    const repick = { kind: 'repick-files', label: 'Pick its files again' } as const;
    // A file the cloud refused is refused again by Try again and by Read the
    // files again. Picking the files again opens the folder, where that file
    // can be left out.
    if (code === 'DOCUMENT_NOT_STORED') return repick;
    switch (classifyFailedQueueRecovery(job)) {
        case 'restore-permission': return { kind: 'restore-permission', label: 'Restore folder access' };
        case 'review-source': return { kind: 'review-source', label: 'Read the files again' };
        case 'retry-saved-intake': return { kind: 'retry', label: 'Try again' };
        case 'continue-quote': return failedJobNeedsFreshPicks(job) ? repick : { kind: 'retry', label: 'Try again' };
        default: return canStartOverFailedJob(job) ? { kind: 'start-over', label: 'Start over' } : repick;
    }
}

// The words for a stopped job end by naming the button its row has. A job
// handed in without a status or id (an action refusal) is read as a row.
function nextStepSentence(job: any): string {
    const step = failedJobNextStep({ ...job, status: 'failed', id: job?.id ?? 0 });
    switch (step.label) {
        case 'Try again': return 'Try again.';
        case 'Start over': return 'Start over runs it again from its file picks.';
        case 'Pick its files again': return 'Pick its files again to set it up afresh.';
        case 'Read the files again': return 'Read the files again reads its folder afresh.';
        default: return step.label ? `${step.label}.` : '';
    }
}

function nextStepLabel(job: any): string {
    return failedJobNextStep({ ...job, status: 'failed', id: job?.id ?? 0 }).label;
}

/**
 * Every failure a Quote Run job can stop on belongs to one family, and each
 * family has one sentence that says what happened, whether saved work is
 * safe, and what the action does (Quote Run SPEC §7, E13). Codes are matched
 * upper-cased: some throw sites use lower case.
 *
 * quote-run-failure-words.test.ts reads every code thrown in batch-queue.js
 * and pipeline-runner.js (including a fallback after `||`) and every
 * failBatch code in the App stage hooks, and fails when one has no family
 * here, so the sheet never prints a developer line because somebody added a
 * throw. The gateway, AI client and price list codes that reach a row are
 * listed there too.
 */
export type FailureFamily =
    | 'run-wide'
    | 'out-of-step'
    | 'save-not-confirmed'
    | 'interrupted'
    | 'nothing-to-read'
    | 'photos'
    | 'costing'
    | 'folder-source'
    | 'name'
    | 'restart-history'
    | 'page-not-ready'
    | 'final-step'
    | 'review-handoff'
    | 'no-result'
    | 'ai-answer'
    | 'busy-here'
    | 'project-switched'
    | 'no-scope'
    | 'notes-open'
    | 'design-check'
    | 'action-refusal'
    | 'coordination'
    | 'specific'
    | 'generic';

const FAILURE_FAMILY_CODES: Record<Exclude<FailureFamily, 'run-wide' | 'coordination'>, string[]> = {
    'out-of-step': [
        'QUOTE_SCOPE_STALE', 'SCOPE_PROJECTION_STALE',
        'STAGE5_PREPARATION_SOURCE_STALE', 'STAGE5_PREPARATION_STALE', 'STAGE5_PREPARATION_TITLE_STALE',
        'STAGE5_PREPARED_SCOPE_STALE', 'STAGE5_READINESS_STALE', 'WMS_INPUT_LINEAGE_STALE',
        'WMS_SCOPE_PROJECTION_STALE', 'PROJECT_RETRY_MATERIALIZATION_STALE', 'PROJECT_RETRY_REFRESH_STALE',
        'PROJECT_RETRY_REFRESH_ADVANCED',
    ],
    'save-not-confirmed': [
        'PROJECT_BACKEND_SYNC_UNVERIFIED', 'READY_FOR_REVIEW_NOT_PERSISTED', 'RISK_EVIDENCE_PERSISTENCE_UNVERIFIED',
        'STAGE5_DRAFT_NOT_PERSISTED', 'STAGE5_PERSISTENCE_VERIFICATION_FAILED', 'QUOTE_REVIEW_STATE_PERSIST_FAILED',
        'STAGE5_PERSISTENCE_UNVERIFIED', 'COSTING_PERSISTENCE_UNVERIFIED', 'INTAKE_READY_PROOF_MISSING',
        // DEC-098: the Quote Run's AI quote did not read back bound to its scope.
        'QUOTE_SCOPE_BINDING_UNVERIFIED',
        // The request the Quote Run adopted before Stage 3 was not saved (pipeline-runner runStage3).
        'WMS_SOURCE_STEP_STOPPED',
    ],
    interrupted: ['QUEUE_LEASE_LOST', 'QUEUE_LIVE_LEASE_CONFLICT', 'QUEUE_PROJECT_ACTIVE', 'PROJECT_RUN_INTERRUPTED'],
    'nothing-to-read': ['ANALYSIS_INPUT_EMPTY', 'SELECTED_FILE_SCOPE_EMPTY'],
    photos: ['PHOTO_FORENSIC_COVERAGE_INCOMPLETE'],
    costing: [
        'COSTING_PRICING_READER_MISSING', 'COSTING_PRICING_SNAPSHOT_MISSING', 'WMS_PRICING_FINGERPRINT_MISSING',
        'WMS_PRICING_AUTHORITY_UNAVAILABLE',
    ],
    // A stage handler that resolved with nothing it could use
    // (pipeline-runner.js requireDispatchedStageResult, and the hooks' own
    // "no result" and "stopped before producing a quote" failures).
    'no-result': ['STAGE_HANDLER_NO_RESULT', 'ANALYSIS_NO_RESULT', 'WMS_NO_RESULT', 'QUOTE_GENERATION_REJECTED'],
    // The model's answer ran out of room or came back empty (ai-api.js).
    'ai-answer': ['AI_TRUNCATED', 'AI_EMPTY_CONTENT'],
    // The hooks' single-flight guards: another AI step on this page.
    'busy-here': ['QUOTE_ALREADY_RUNNING', 'ANALYSIS_ALREADY_RUNNING', 'WMS_ALREADY_RUNNING'],
    'project-switched': ['ANALYSIS_PROJECT_CHANGED', 'WMS_PROJECT_CHANGED'],
    'no-scope': ['QUOTE_SCOPE_EMPTY', 'WMS_SCOPE_EMPTY'],
    'notes-open': ['ANALYSIS_INTERACTIVE_PAUSE'],
    'design-check': ['WMS_AUTHORITY_PREFLIGHT'],
    'folder-source': [
        'DEFERRED_INTAKE_MATERIALIZER_UNAVAILABLE', 'DEFERRED_INTAKE_PHASE_INVALID', 'DEFERRED_INTAKE_SOURCE_MISSING',
        'DEFERRED_SOURCE_RESERVED', 'INTAKE_RETRY_REQUIRES_SOURCE_REFRESH', 'PROJECT_RETRY_RECREATE_IDENTITY_MISMATCH',
        'PROJECT_RETRY_REFRESH_IDENTITY_MISMATCH',
    ],
    name: ['PROJECT_TITLE_UNRESOLVED'],
    'restart-history': ['PROJECT_RESTART_LINEAGE_CONFLICT', 'PROJECT_RESTART_LINEAGE_INVALID'],
    'page-not-ready': ['PIPELINE_HANDLER_MISSING', 'PROJECT_QUEUE_HISTORY_UNAVAILABLE'],
    'final-step': ['STAGE5_PREPARATION_MISSING', 'STAGE5_PREPARATION_INCOMPLETE'],
    'review-handoff': ['QUOTE_REVIEW_REQUIRED'],
    'action-refusal': [
        'NO_STAGED_ROW', 'STAGED_TRANSITION_LOST', 'QUEUE_JOB_DISMISSED',
        'PROJECT_DELETED_TOMBSTONE', 'PROJECT_DELETE_QUEUE_ACTIVE',
    ],
    // Each has its own sentence in operatorFailureText below.
    specific: [
        'SOURCE_PERMISSION_REQUIRED', 'SOURCE_CHANGED', 'FILE_MATERIALISATION_INCOMPLETE',
        'FILE_PREPROCESS_BUILD_INCOMPLETE', 'PROJECT_NOT_LOCAL', 'PROJECT_STALE_WRITE', 'WMS_SCOPE_STALE',
        'WMS_REGEN_BUILDER_CONFIRMATION_REQUIRED', 'RFQ_PREFLIGHT_BLOCKED', 'STAGE_TIMEOUT',
        'PROJECT_BACKEND_SYNC_FAILED', 'INTAKE_HANDOFF_REQUIRED', 'PROJECT_PDF_EVIDENCE_INCOMPLETE',
        'DOCUMENT_BINARY_PERSISTENCE_FAILED', 'PROJECT_CONFLICT', 'PROJECT_BACKEND_NEWER_THAN_LOCAL',
        'PROJECT_VIDEO_SOURCE_UNAVAILABLE', 'PROJECT_SUMMARY_PERSISTENCE_UNVERIFIED',
        'UNSYNCED_FOLDER', 'INTAKE_HISTORY_RETIRED', 'PROJECT_QUOTE_RUN_RELEASE_FAILED',
        'COSTING_SCOPE_STALE', 'WMS_PRICING_AUTHORITY_CHANGED', 'DOCUMENT_NOT_STORED',
        'QUOTE_BRAIN_PROJECT',
    ],
    // Listed as generic on purpose: the engine's own "no better code" code,
    // and the backend reconciliation stops of project-intake-persistence.js
    // (thrown while a job's files are read, or before its draft loads) that
    // have no plain words yet. The generic words say so and name the row's
    // own button.
    generic: [
        'PIPELINE_STAGE_FAILED',
        'PROJECT_BACKEND_AUTHORITATIVE_INPUT_CONFLICT', 'PROJECT_BACKEND_GENERATED_STATE_CONFLICT',
        'PROJECT_BACKEND_IDENTITY_MISMATCH', 'PROJECT_BACKEND_PROJECT_IDENTITY_CONFLICT', 'PROJECT_ID_INVALID',
        'PROJECT_IDENTITY_CONFLICT', 'PROJECT_LOCAL_CHANGED_DURING_BACKEND_RECONCILIATION',
        'PROJECT_RESTART_LINEAGE_NOT_PERSISTED',
    ],
};

/**
 * A family's words in parts, so every sentence is true for the row it is
 * shown on (Quote Run review, slice 2 round 6):
 *   what    what happened, naming no cause the family's throw sites lack;
 *   saved   whether saved work is safe, said only once a stage has run: while
 *           the job's files are read (isReadingFailure) there are no stages;
 *   retry   the next step when the row's button is Try again;
 *   other   the next step for any other button, from its label. Without it
 *           the row's own step sentence (nextStepSentence) is used.
 *   again   what to do if Try again stops the same way; it names a stage, so
 *           it is said only for a quote stage's Try again.
 * The next step always names the button the row has (failedJobNextStep), so
 * no sentence sends the operator to a Try again the row does not offer.
 */
type FamilyWords = {
    what: string;
    whatWhileReading?: string;
    saved?: string;
    savedWhileReading?: string;
    retry?: string;
    other?: (label: string) => string;
    again?: string;
};

const FAMILY_WORDS: Partial<Record<FailureFamily, FamilyWords>> = {
    // Thrown when a receipt that binds one saved stage to the next is missing
    // or stale (pipeline-runner.js runStage4, runStage5 and
    // markReadyForBuilderReview), not only when something changed, so the
    // words say what QuoteMaker could not confirm instead of naming a cause.
    'out-of-step': {
        what: "QuoteMaker could not confirm that this job's saved stages still match each other, so it stopped rather than build on them.",
        whatWhileReading: "QuoteMaker could not confirm that this job's saved files still match its folder, so it stopped rather than build on them.",
        saved: 'Finished stages are saved.',
    },
    'save-not-confirmed': {
        what: 'QuoteMaker could not confirm a save, so it stopped rather than build on it.',
        saved: 'Earlier stages are saved.',
    },
    interrupted: {
        what: 'This job was interrupted, or another tab picked it up. Nothing was overwritten.',
        other: (label) => `${label} when no other tab is running it.`,
    },
    'nothing-to-read': {
        what: 'This job has nothing QuoteMaker can read yet: no notes, documents or photos were picked.',
        other: (label) => `${label}.`,
    },
    photos: {
        what: 'Not every photo was checked, so QuoteMaker stopped before building on them.',
        saved: 'Finished stages are saved.',
    },
    costing: {
        what: 'The costing could not be checked against your price list.',
        again: 'If it stops the same way, open the job at Stage 4.',
    },
    'folder-source': {
        what: "QuoteMaker could not read this job's folder again.",
        retry: 'Press Rescan, then Try again.',
    },
    name: {
        what: "QuoteMaker could not work out this job's name.",
        retry: 'Open it, give it a name, then Try again.',
    },
    'page-not-ready': {
        what: 'Quote Run was not ready on this page.',
        retry: 'Close Quote Run, open it again, then Try again.',
        other: (label) => `Close Quote Run and open it again, then ${label}.`,
    },
    'final-step': {
        what: "The draft's last step was not prepared.",
        saved: 'Finished stages are saved.',
    },
    'busy-here': {
        what: 'Another AI step was already running on this page, so QuoteMaker did not start a second one.',
        saved: 'Finished stages are saved.',
        other: (label) => `${label} when it finishes.`,
    },
    'project-switched': {
        what: 'Another project was opened on this page while this job ran, so QuoteMaker stopped rather than mix them up.',
        saved: 'Finished stages are saved.',
    },
    // project-intake-persistence.js restartLineageConflict and batch-queue.js
    // _normaliseRestartLineage: the job's record of the attempt it restarts
    // names more than one earlier attempt, or none that can be right. It is
    // worked out again from the project and the queue history each time, and
    // the row's own step changes neither, so the words say where the fix is
    // and that the step stops the same way until then. (The engine's own note
    // names Project History, a page the Run Sheet replaced.)
    'restart-history': {
        what: "This job's record of its earlier attempts does not line up, so QuoteMaker stopped rather than guess which attempt it replaces.",
        other: (label) => `QuoteMaker support can sort that out. Until then, ${label} stops the same way.`,
    },
};

// Families whose words do not end in the row's button: a Quote Run button
// does not fix them, and each sentence says where the fix is.
const FAMILY_SENTENCE: Partial<Record<FailureFamily, string>> = {
    'review-handoff': 'Stage 4 needs a look before the final quote. Open Stage 4 to check the scope.',
};

function familyText(words: FamilyWords, job: any): string {
    const reading = isReadingFailure(job);
    const label = nextStepLabel(job);
    let next: string;
    if (label === 'Try again') {
        next = words.retry || words.other?.(label) || nextStepSentence(job);
        if (words.again && !reading) next = `${next} ${words.again}`;
    } else {
        next = label && words.other ? words.other(label) : nextStepSentence(job);
    }
    return [
        reading && words.whatWhileReading ? words.whatWhileReading : words.what,
        reading ? words.savedWhileReading : words.saved,
        next,
    ].filter(Boolean).join(' ');
}

// pipeline-runner.js createCostingFreshnessError: the price list could not be
// read, the costing is not bound to the final scope, or its one automatic
// re-costing timed out, was refused, returned nothing or stayed stale.
const COSTING_NOT_CONFIRMED: FamilyWords = {
    what: "QuoteMaker could not confirm this job's costing matches its final scope and your current price list, so it stopped rather than finish the draft.",
    saved: 'Finished stages are saved.',
    again: 'If it stops the same way, open the job at Stage 4 to check its costing.',
};

// Families whose next step depends on whether the row can offer Try again:
// the sentence ends in Try again only when it can (canRetryFailedQueueJob).
const FAMILY_SENTENCE_BEFORE_RETRY: Partial<Record<FailureFamily, string>> = {
    'ai-answer': "The AI's answer came back cut short or empty, so QuoteMaker stopped rather than build on it. Finished stages are saved.",
    'no-scope': 'QuoteMaker found no scope items for this job, so there was nothing to cost or quote. Finished stages are saved.',
    'notes-open': 'Notes for this job were still being edited on this page, so QuoteMaker did not start Stage 1. Finish the notes first.',
    // The old Work Method's design check (use-wms-generation.ts, gone with the
    // old pipeline): a stored row can still carry it.
    'design-check': 'A design document for this job needed a look before the Work Method, so QuoteMaker stopped. Finished stages are saved.',
};

// Run-wide stops in the same parts. Their fix is outside the job, so the next
// step says what to do first, then names the row's own button.
const NOT_SET_UP: FamilyWords = {
    what: "QuoteMaker's AI service is not set up for this business.",
    saved: 'Finished stages are saved.',
    other: (label) => `This needs QuoteMaker support. ${label} once it is fixed.`,
};
const STAYED_BUSY: FamilyWords = {
    what: 'The AI service stayed busy or out of reach, even after QuoteMaker waited and tried again.',
    saved: 'Finished stages are saved.',
    other: (label) => `${label} in a few minutes.`,
};
const CREDIT_OUT: FamilyWords = {
    what: 'The AI credit for this business has run out.',
    saved: 'Nothing more was charged, and finished stages are saved.',
    savedWhileReading: 'Nothing more was charged.',
    other: (label) => `Contact QuoteMaker support to top up, then ${label}.`,
};

const RUN_WIDE_WORDS: Record<string, FamilyWords> = {
    AI_CREDIT_EXHAUSTED: CREDIT_OUT,
    UPSTREAM_402: CREDIT_OUT,
    // A codeless 401 or 403 is read as this code (batch-queue.js
    // _normaliseBatchError), including one from the backend while a job's
    // files are read, where no AI runs.
    AI_AUTH_FAILED: {
        what: "Your sign-in has expired, or QuoteMaker's AI service turned the request away.",
        whatWhileReading: "Your sign-in has expired, or QuoteMaker's service turned the request away, while this job's files were read.",
        saved: 'Finished stages are saved.',
        other: (label) => `Sign in again, then ${label}. If it stops the same way, contact QuoteMaker support.`,
    },
    AI_PROVIDER_NOT_CONFIGURED: NOT_SET_UP,
    AI_NO_MATCHING_ENDPOINT: NOT_SET_UP,
    NO_MATCHING_ENDPOINTS: NOT_SET_UP,
    ALL_KEYS_QUARANTINED: NOT_SET_UP,
    // The provider refused QuoteMaker's own key, not the operator's sign-in.
    UPSTREAM_401: NOT_SET_UP,
    UPSTREAM_403: NOT_SET_UP,
    MODEL_NOT_ALLOWED: NOT_SET_UP,
    FEATURE_NOT_ALLOWED: NOT_SET_UP,
    AI_KEY_MISSING: NOT_SET_UP,
    AI_RATE_LIMITED: STAYED_BUSY,
    UPSTREAM_429: STAYED_BUSY,
    UPSTREAM_PROVIDER_RATE_LIMITED: {
        what: 'The provider behind one AI step stayed busy, even after QuoteMaker waited and tried again.',
        saved: 'Finished stages are saved.',
        other: (label) => `${label} in a few minutes, or choose another model for that step on the AI Models page. Adding AI keys does not help.`,
    },
    UPSTREAM_TIMEOUT: STAYED_BUSY,
    UPSTREAM_INVALID_JSON: STAYED_BUSY,
    UPSTREAM_STREAM_ERROR: STAYED_BUSY,
    // The daily sum resets on the database clock (ai-usage-ledger.ts
    // dayToDateCents), which need not be midnight in the office, so the words
    // promise no clock time.
    DAILY_LIMIT_REACHED: {
        what: "Today's AI budget for this business is used up, so QuoteMaker stopped before spending more.",
        saved: 'Finished stages are saved.',
        other: (label) => `The budget resets once a day. ${label} later, or contact QuoteMaker support to lift the daily limit.`,
    },
    HARD_LIMIT_REACHED: {
        what: "This month's AI budget for this business is used up, so QuoteMaker stopped before spending more.",
        saved: 'Finished stages are saved.',
        other: (label) => `New AI work can start on the 1st of next month, or contact QuoteMaker support to lift the monthly limit. Then ${label}.`,
    },
    QM_PRICING_DATA_UNAVAILABLE: {
        what: 'QuoteMaker could not find a price list for this business, so it stopped before costing.',
        saved: 'Finished stages are saved.',
        other: (label) => `Add your prices to the price list, then ${label}.`,
    },
    // NETWORK_FAILED is this computer's connection or QuoteMaker's own service
    // (batch-queue.js: HTTP 502 or 503, or "network", "failed to fetch",
    // "offline"), not the AI service. The reading phase makes no AI call.
    NETWORK_FAILED: {
        what: 'QuoteMaker could not reach its service, even after it waited and tried again.',
        whatWhileReading: "QuoteMaker could not reach its service while reading this job's files, even after it waited and tried again.",
        saved: 'Finished stages are saved.',
        other: (label) => `Check the internet connection, then ${label}.`,
    },
};

// What the chosen model cannot do, by the gateway's code. The words are the
// gateway's own reason (ai-error-handling modelCapabilityMessage, the same
// sentence the gateway sends), naming the model the job recorded.
const MODEL_CAPABILITY_GAPS: Readonly<Record<string, ModelCapabilityGap>> = {
    MODEL_NO_TOOL_SUPPORT: 'tools',
    MODEL_NO_IMAGE_SUPPORT: 'images',
    MODEL_NO_PARAMETER_SUPPORT: 'parameters',
    MODEL_NO_ENDPOINTS: 'endpoints',
};

function runWideSentence(code: string, job: any): string | undefined {
    const gap = MODEL_CAPABILITY_GAPS[code];
    if (gap) {
        const model = job?.lastErrorDetails?.model;
        return familyText({
            what: modelCapabilityMessage(typeof model === 'string' ? model : null, gap),
            saved: 'Finished stages are saved.',
            other: (label) => `${label} once the model for this task is changed on the AI Models page.`,
        }, job);
    }
    const words = RUN_WIDE_WORDS[code] || (UPSTREAM_SERVER_ERROR.test(code) ? STAYED_BUSY : undefined);
    return words ? familyText(words, job) : undefined;
}

/** Action refusals toast on the action and never become a row (SPEC §7). */
export const ACTION_REFUSAL_CODES: readonly string[] = Object.freeze([...FAILURE_FAMILY_CODES['action-refusal']]);

const ACTION_REFUSAL_TEXT: Record<string, string> = {
    NO_STAGED_ROW: 'That change did not save. The list has been refreshed. Try again.',
    STAGED_TRANSITION_LOST: 'That change did not save. The list has been refreshed. Try again.',
    QUEUE_JOB_DISMISSED: 'This job was removed from Quote Run. Pick its folder again to run it.',
    PROJECT_DELETED_TOMBSTONE: 'This project was deleted. Press Rescan and pick the folder again to make a new one.',
    PROJECT_DELETE_QUEUE_ACTIVE: 'This project is in the run right now. Stop the run or wait for it, then delete it.',
};

/**
 * A Try again, Restore folder access, Add back or Hide the engine refused
 * before writing anything (batch-queue.js retryFailed, requeueTerminalJob,
 * dismissStoppedProject). These codes can also be a row's own stop, where the
 * family sentence is right; as a refusal of an action the operator just took,
 * each says why and names a next step that does not refuse the same way.
 */
const REFUSED_ACTION_TEXT: Record<string, string> = {
    PROJECT_RETRY_MATERIALIZATION_STALE: "This job's saved files no longer match its folder, so QuoteMaker did not start it again. Read the files again from its folder first.",
    PROJECT_RETRY_REFRESH_STALE: 'This project changed while its folder was being read, so QuoteMaker did not start it again. Read the files again from its folder first.',
    INTAKE_RETRY_REQUIRES_SOURCE_REFRESH: 'This job has to read its folder again before it can run, so QuoteMaker did not start it. Read the files again from its folder first.',
    PROJECT_RETRY_REFRESH_ADVANCED: 'This project already has quote work saved, so QuoteMaker did not read its files again. Open it from the Run Sheet to carry on from its saved stage.',
    DEFERRED_SOURCE_RESERVED: 'Another job in Quote Run already uses this folder, so this one was not started again. Let that job finish, or hide this one from the list.',
    QUEUE_PROJECT_ACTIVE: 'This project still has a job on the run list or running, so it was not hidden. Stop the run, or take that job off the list first.',
    // Clear (batch-queue.js clearAll) refuses while any job holds a live
    // lease: another tab's run, or a run that stopped (this tab's, or a page
    // that closed) whose lease has not run out. A lease lasts 90 s.
    QUEUE_LIVE_LEASE_CONFLICT: 'Nothing was cleared. A job in this list is still held by a run that is going in another tab, or that stopped less than 90 s ago. Try again when that run has finished, or in 90 s.',
};

/**
 * The toast when this tab's run throws instead of ending (the start handler's
 * catch). One failed lease renewal ends it with QUEUE_LEASE_LOST and leaves
 * the job it was running part way, and that job's row says what to do. Any
 * other throw names no engine text.
 */
export function runEndedEarlyText(error: any): string {
    const code = String(error?.code || error?.errorCode || '').trim().toUpperCase();
    if (code === 'QUEUE_LEASE_LOST') {
        return "The run in this tab stopped part way through a job. That job's row in the list says what to do next.";
    }
    return 'The run did not finish. Each job in the list shows where it is now.';
}

/**
 * The end-of-run message when a run-wide fault ended the run with jobs it
 * never started (the drain's keptNotStarted, batch-queue.js
 * requestStopAfterCurrentJob keepNotStarted): the run stopped, what it
 * finished first, how many jobs never started, and that they still wait to
 * start. Null for any other ending, which keeps its own summary. A fault on
 * the run's last job keeps none, so its run ends with the ordinary summary.
 */
export function runStoppedOnFaultText(detail: any): string | null {
    if (detail?.keptNotStarted !== true) return null;
    const whole = (value: unknown) => Math.max(0, Math.floor(Number(value) || 0));
    const count = whole(detail?.notStarted);
    if (count === 0) return null;
    const completed = whole(detail?.totalCompleted);
    const failed = whole(detail?.totalFailed);
    const skipped = whole(detail?.totalSkipped);
    // While files were read, a job whose files were read was not drafted
    // either: the engine counts it among the jobs kept, not as finished.
    const reading = detail?.phase === 'pre-processing';
    const finished = [
        !reading && completed > 0 ? `${completed} handed to QuoteMaker` : '',
        failed > 0 ? `${failed} failed${reading ? ` while ${failed === 1 ? 'its' : 'their'} files were read` : ''}` : '',
        skipped > 0 ? `${skipped} skipped` : '',
    ].filter(Boolean);
    const before = finished.length > 0 ? ` Before it stopped: ${finished.join(', ')}.` : '';
    const jobs = count === 1 ? '1 job' : `${count} jobs`;
    const kept = reading
        ? `${jobs} ${count === 1 ? 'was' : 'were'} not drafted.`
        : `${jobs} never started.`;
    const waiting = count === 1 ? 'It is still waiting to start.' : 'They are still waiting to start.';
    return `The run stopped because a job met a problem every job would meet.${before} ${kept} ${waiting} `
        + "The stopped job's row says what to fix, then start the run again.";
}

// The saved-intake retry refusals that the folder can fix: reading the files
// again from the current folder (the review path) is the way on.
const RETRY_NEEDS_FOLDER_READ = new Set([
    'PROJECT_RETRY_MATERIALIZATION_STALE',
    'PROJECT_RETRY_REFRESH_STALE',
    'INTAKE_RETRY_REQUIRES_SOURCE_REFRESH',
]);

/** True when a refused retry needs the job's folder read again, not another retry. */
export function retryRefusalNeedsFolderRead(error: any): boolean {
    return RETRY_NEEDS_FOLDER_READ.has(String(error?.code || error?.errorCode || '').trim().toUpperCase());
}

const FAMILY_BY_CODE = new Map<string, FailureFamily>();
for (const [family, codes] of Object.entries(FAILURE_FAMILY_CODES) as Array<[FailureFamily, string[]]>) {
    for (const code of codes) FAMILY_BY_CODE.set(code, family);
}
for (const code of RUN_WIDE_FAILURE_CODES) FAMILY_BY_CODE.set(code, 'run-wide');
for (const code of ['PROJECT_QUOTE_RUN_BUSY', ...QUOTE_RUN_REVIEW_CODES]) FAMILY_BY_CODE.set(code, 'coordination');

/** The failure family of a code, or null when nobody has given it words. */
export function failureFamilyFor(code: unknown): FailureFamily | null {
    const key = String(code ?? '').trim().toUpperCase();
    if (!key) return null;
    return FAMILY_BY_CODE.get(key) ?? (UPSTREAM_SERVER_ERROR.test(key) ? 'run-wide' : null);
}

function stageNumberOf(job: any): number | null {
    const stage = Number(job?.lastErrorStage ?? job?.currentStage);
    return Number.isFinite(stage) && stage > 0 ? stage : null;
}

function canRetryAsRow(job: any): boolean {
    return canRetryFailedQueueJob({ ...job, status: 'failed', id: job?.id ?? 0 });
}

// While a job's files are read (the pre-processing phase, or a stop recorded
// at the reading or preprocess step) no quote stage has run, so its words
// claim no saved stages.
function isReadingFailure(job: any): boolean {
    const stageValue = job?.lastErrorStage ?? job?.currentStage;
    return (job?.phase || 'prepare-quote') === 'pre-processing'
        || stageValue === 'materialize-files'
        || stageValue === 'preprocess';
}

function genericFailureText(job: any): string {
    const reading = isReadingFailure(job);
    const stage = stageNumberOf(job);
    const lead = reading
        ? 'Stopped while its files were read, on a problem QuoteMaker has no plain words for yet.'
        : stage
            ? `Stopped at Stage ${stage} on a problem QuoteMaker has no plain words for yet.`
            : 'QuoteMaker hit a problem it has no plain words for yet.';
    // The row's own button (failedJobNextStep), never a Try again it cannot
    // give. The words name no Technical detail: the Queue drawer and the
    // headline print them too, and only the job panel has that control.
    const next = nextStepSentence(job);
    const parts = reading ? [lead, next] : [lead, 'The stages already saved are kept.', next];
    return parts.filter(Boolean).join(' ');
}

// Why the file service refused a file, from its refusal code and status
// (backend cloud-media-upload-policy.ts and files.js).
function documentRefusalReason(details: any): string {
    const code = String(details?.causeCode || '').toLowerCase();
    const status = Number(details?.causeStatus);
    if (code.includes('mime_not_permitted') || status === 415) return 'QuoteMaker does not store this type of file.';
    if (code.includes('signature')) return 'Its contents do not match its file type.';
    if (status === 413 || /size|too_large/.test(code)) return 'It is too large to store.';
    return 'The cloud service turned it away.';
}

/**
 * A file the cloud refused to store (DOCUMENT_NOT_STORED, 2026-10-04 UAT F2):
 * which file, that it was not stored and why, and the step that works. It is
 * almost always met while the job's files are read, before any quote stage;
 * a save during drafting can meet it too (batch-queue.js), and then the words
 * name the stage.
 */
function documentNotStoredText(job: any): string {
    const name = String(job?.lastErrorDetails?.documentName || '').trim();
    const label = nextStepLabel(job);
    const stage = isReadingFailure(job) ? null : stageNumberOf(job);
    return [
        `QuoteMaker could not store ${name || 'one of its files'}, so this job stopped ${stage ? `at Stage ${stage}` : 'before drafting'}.`,
        documentRefusalReason(job?.lastErrorDetails),
        label === 'Pick its files again'
            ? 'Pick its files again and leave that file out, or save it as a PDF or Word file first.'
            : nextStepSentence(job),
    ].filter(Boolean).join(' ');
}

/**
 * Operator-language text for a failed queue job. Every failure code has a
 * family sentence; a code no family knows gets the generic sentence, never the
 * raw engine line (SPEC §7, G10). The raw line lives only under Technical
 * detail. Null only when the job recorded nothing at all, so nothing is
 * invented. Every string answers: what happened, is my work safe, and what
 * does the action do?
 */
export function operatorFailureText(job: any): string | null {
    if (!job) return null;
    const code = String(job?.lastErrorCode || '').trim().toUpperCase();
    const raw = String(job?.lastError || '');
    if (code === 'SOURCE_PERMISSION_REQUIRED') {
        if (!hasReconnectableDeferredSource(job)) {
            return 'The saved folder connection is no longer available. Review the current source folder files before trying again.';
        }
        return 'QuoteMaker no longer has access to the queued folder. Restore folder access, then the same saved intake can continue.';
    }
    if (code === 'SOURCE_CHANGED') {
        return 'The source files changed after this job was queued. Review the current folder files before trying again.';
    }
    if (code === 'FILE_MATERIALISATION_INCOMPLETE') {
        return 'QuoteMaker could not finish reading every selected source file. Review the current folder files before trying again.';
    }
    if (code === 'FILE_PREPROCESS_BUILD_INCOMPLETE') {
        return 'QuoteMaker read the source but could not build a complete intake. Review the unreadable or changed files before trying again.';
    }
    if (code === 'PROJECT_NOT_LOCAL') {
        return (job?.phase || 'prepare-quote') === 'pre-processing'
            ? 'The saved local intake is missing. Review the current source folder so QuoteMaker can rebuild it safely.'
            : "This job's saved project is no longer in this browser, so QuoteMaker cannot carry on with it. Pick its files again to set it up afresh.";
    }
    if (code === 'PROJECT_STALE_WRITE') {
        return 'Another tab or device saved this project first, so nothing here was overwritten. Retry reloads the newest version and continues from where it left off.';
    }
    // A dev-server / app update mid-run leaves the page holding a module
    // graph the server no longer serves; the lazy Stage 5 import then fails.
    if (/dynamically imported module/i.test(raw)) {
        return 'QuoteMaker updated while this project was running. Reload this page, then retry. Completed stages are kept.';
    }
    // pipeline-runner's load settle guard. Its message is developer-speak
    // ("currentProjectIdRef did not flip") and its /timeout/ substring makes
    // the classifier file it under STAGE_TIMEOUT: translate before the
    // generic timeout copy below can claim it.
    if (/currentProjectIdRef did not flip/i.test(raw)) {
        return 'The project took too long to open because the app was busy. Nothing was lost. Try again.';
    }
    // Each ends with the row's own button (failedJobNextStep), which is never
    // called Retry: Try again, or Start over when the stop is final.
    if (code === 'WMS_SCOPE_STALE') {
        return 'The Work Method and the saved scope did not match when Stage 3 finished. The final quote was not started. '
            + nextStepSentence(job);
    }
    // use-wms-generation.ts assertCurrentPricingAuthority: the price list
    // changed while Stage 3 ran, so its result was thrown away before it was
    // saved. Nothing is out of step; the next attempt uses the new prices.
    if (code === 'WMS_PRICING_AUTHORITY_CHANGED') {
        return 'Your price list changed while the Work Method was being written, so QuoteMaker set that result aside. '
            + `Finished stages are saved. ${nextStepSentence(job)}`;
    }
    if (code === 'WMS_REGEN_BUILDER_CONFIRMATION_REQUIRED') {
        return `This project has builder-edited scope work, so QuoteMaker stopped before replacing it. ${nextStepSentence(job)}`;
    }
    if (code === 'RFQ_PREFLIGHT_BLOCKED') {
        return 'QuoteMaker could not confirm a Request for Quote in the selected files. Open the project to add the RFQ or make the logged “Run anyway” decision before retrying.';
    }
    // STAGE_TIMEOUT and PROJECT_CONFLICT can also be recorded while the files
    // are read: no stages exist yet, and the row's button is its own.
    if (code === 'STAGE_TIMEOUT') {
        if (isReadingFailure(job)) {
            return `Reading this job's files stopped making progress and was cut off. ${nextStepSentence(job)}`;
        }
        return 'A stage stopped making progress and was cut off. Completed stages are kept. Try again.';
    }
    if (code === 'DOCUMENT_NOT_STORED') return documentNotStoredText(job);
    // The Quote Run writes the summary and stops (D2, docs/old-pipeline-removal/PLAN.md): QuoteMaker writes the job's
    // quote on the server, or the builder writes it by hand. Nothing broke, and no button here changes that. The
    // runner's own words say which, so they are shown as they are.
    if (code === 'QUOTE_BRAIN_PROJECT') {
        return raw.trim()
            || "The summary is saved. QuoteMaker writes this job's quote on the server, so the Quote Run stops here for it.";
    }
    if (code === 'PROJECT_BACKEND_SYNC_FAILED') {
        // Ends in the row's own button: Try again while the stop can clear
        // (an outage), else the step the job has (a refusal is final).
        const next = nextStepLabel(job) === 'Try again' ? 'Check your connection, then try again.' : nextStepSentence(job);
        return `QuoteMaker could not register this project with the cloud service. Your local files were saved. ${next}`.trim();
    }
    // intake-project-handoff.js: an RFQ quote is made by Intake's handoff, so
    // Quote Run offers no button for it (failedJobNextStep).
    if (code === 'INTAKE_HANDOFF_REQUIRED') {
        return 'This quote request came through Intake, so its quote is made there once the site visit photos and notes or report are in. '
            + 'Open Intake from the menu to finish it.';
    }
    // The next three name the row's own button: Read the files again while
    // its files are read, Try again while its draft is written.
    if (code === 'PROJECT_PDF_EVIDENCE_INCOMPLETE') {
        return `Some visual pages in the PDF could not be fully analysed. ${nextStepLabel(job)} to finish reading the PDF.`;
    }
    if (code === 'DOCUMENT_BINARY_PERSISTENCE_FAILED') {
        return `Documents could not be stored to local cache. Ensure disk space is available, then ${nextStepLabel(job)}.`;
    }
    if (code === 'PROJECT_CONFLICT') {
        if (isReadingFailure(job)) {
            return `The cloud record changed while this job's files were read. Nothing was lost. ${nextStepSentence(job)}`;
        }
        return `The cloud record changed during processing. Nothing was lost. ${nextStepSentence(job)}`;
    }
    // The five codes below were all met by a real 16-folder run on 2026-09-20
    // and had no plain-words copy, so the sheet printed the developer message.
    if (code === 'PROJECT_BACKEND_NEWER_THAN_LOCAL') {
        return 'This project already has quote work saved in the cloud, so QuoteMaker did not replace it with a fresh intake. Open the job to carry on with it.';
    }
    if (code === 'PROJECT_VIDEO_SOURCE_UNAVAILABLE') {
        return 'A site video could not be read from where it is stored. '
            + `Reconnect the source folder or take that video out of the selection, then ${nextStepLabel(job)}.`;
    }
    if (code === 'PROJECT_SUMMARY_PERSISTENCE_UNVERIFIED') {
        return 'QuoteMaker could not confirm the project summary was saved, so it stopped rather than build on it. The earlier stages are kept. Try again.';
    }
    // batch-queue.js preflight: the job names a folder that was never read in.
    if (code === 'UNSYNCED_FOLDER') {
        return 'This job points at a folder QuoteMaker has not read in yet, so there was nothing to build on.';
    }
    if (code === 'INTAKE_HISTORY_RETIRED') {
        return 'This project moved past reading its files outside Quote Run, so this step was closed. Nothing was lost.';
    }
    if (code === 'PROJECT_QUOTE_RUN_RELEASE_FAILED') {
        return 'QuoteMaker could not confirm it let go of this project when the job ended. Nothing was lost. Try again in a minute.';
    }
    // pipeline-runner.js: none of the queued file ids still match the saved
    // project. Files WERE picked, so the sentence must not say otherwise.
    if (code === 'SELECTED_FILE_SCOPE_EMPTY') {
        return 'None of the files picked for this job are in its saved project any more, so QuoteMaker had nothing to read. Pick its files again.';
    }
    if (code === 'COSTING_SCOPE_STALE') return familyText(COSTING_NOT_CONFIRMED, job);
    // Run-wide stops: the credit, the budget, the sign-in, the connection or
    // the AI service, not the job. AI_AUTH_FAILED no longer mentions an AI
    // key: customers never hold one.
    const runWide = runWideSentence(code, job);
    if (runWide) return runWide;
    const coordination = quoteRunCoordinationOutcome(job);
    if (coordination) return coordination.description;
    const refusal = ACTION_REFUSAL_TEXT[code];
    if (refusal) return refusal;
    const family = failureFamilyFor(code);
    // These end by naming the row's own button, whichever it is: Try again
    // when the row can give it, else Start over, Pick its files again or
    // Read the files again. None stops without a next step.
    if (family === 'no-result') {
        const stage = stageNumberOf(job);
        const lead = stage
            ? `Stage ${stage} ended without a result QuoteMaker could use, so it stopped.`
            : 'A stage ended without a result QuoteMaker could use, so it stopped.';
        return `${lead} Finished stages are saved. ${nextStepSentence(job)}`;
    }
    const beforeRetry = family ? FAMILY_SENTENCE_BEFORE_RETRY[family] : undefined;
    if (beforeRetry) return `${beforeRetry} ${nextStepSentence(job)}`;
    // Each ends by naming the row's own button, and claims no saved stages
    // while the job's files are read (familyText).
    const familyWords = family ? FAMILY_WORDS[family] : undefined;
    if (familyWords) return familyText(familyWords, job);
    const familySentence = family ? FAMILY_SENTENCE[family] : undefined;
    if (familySentence) return familySentence;
    if (!code && !raw.trim()) return null;
    return genericFailureText(job);
}

function firstSentenceOf(text: string): string {
    const trimmed = String(text || '').trim();
    const match = trimmed.match(/^(.+?[.!?])(?=\s|$)/);
    return (match ? match[1]! : trimmed).trim();
}

/**
 * The toast when a job stops during a run (the engine's qm-batch-job-failed
 * event). It never prints the engine's own line: that sits under the job
 * panel's Technical detail. A refused file names the file and its one next
 * step, which its code fixes. Any other stop says what happened and points at
 * its row, whose next step depends on the job's saved state. With
 * `overlayClosed` (the app-level toast, Import Projects not on screen) it
 * says to open Import Projects where it would point at a row in the list.
 */
export function failedJobToastText(detail: any, { overlayClosed = false }: { overlayClosed?: boolean } = {}): string {
    const name = String(detail?.displayMeta?.name || '').trim();
    const code = String(detail?.errorCode || '').trim().toUpperCase();
    const job = {
        id: detail?.jobId ?? 0,
        status: 'failed',
        projectId: detail?.projectId ?? null,
        phase: detail?.phase || 'prepare-quote',
        lastErrorCode: code,
        lastError: '',
        lastErrorStage: detail?.stage ?? null,
        lastErrorRetryable: typeof detail?.retryable === 'boolean' ? detail.retryable : null,
        lastErrorDetails: detail?.errorDetails || null,
    };
    if (code === 'DOCUMENT_NOT_STORED') {
        return `${name || 'A queued project'}: ${documentNotStoredText(job)}${overlayClosed ? ' Open Import Projects to do that.' : ''}`;
    }
    const what = code ? firstSentenceOf(operatorFailureText(job) || '') : '';
    const where = overlayClosed ? 'Open Import Projects to see what to do next.' : 'Its row in the list says what to do next.';
    if (!what) return `${name || 'A queued project'} stopped. ${where}`;
    return `${name || 'A queued project'}: ${what} ${where}`;
}

/**
 * The toast for an action the engine refused (Leave out, Add back, Try
 * again...). The five refusal codes have their own words, a refused retry,
 * requeue or hide says why and what to do next, and any other coded refusal
 * gets its family sentence. A plain message without a code is the surface's
 * own words and is kept.
 */
export function actionFailureText(error: any): string {
    const code = String(error?.code || error?.errorCode || '').trim().toUpperCase();
    const refusal = code ? (ACTION_REFUSAL_TEXT[code] || REFUSED_ACTION_TEXT[code]) : undefined;
    if (refusal) return refusal;
    const family = failureFamilyFor(code);
    if (family && family !== 'generic') {
        const text = operatorFailureText({ lastErrorCode: code, lastError: error?.message || '' });
        if (text) return text;
    }
    const message = typeof error?.message === 'string' ? error.message.trim() : '';
    if (message && !code) return message;
    return 'That did not work. Nothing was changed.';
}

// The engine writes this into every automatic provider wait it records, so a
// wait is never counted as the operator trying again.
const AUTOMATIC_WAIT = /retrying automatically/i;

/**
 * The retry loop guard (SPEC §7, E22, G14). True when the operator has tried
 * again and the job stopped the same way twice: the row then offers the next
 * recovery path instead of the same retry. A run-wide stop is exempt: its fix
 * is outside the job (credit, sign-in, support), so Try again stays right.
 */
export function isRepeatedFailure(job: any): boolean {
    const code = String(job?.lastErrorCode || '').trim().toUpperCase();
    if (!code || (Number(job?.retryCount) || 0) < 1) return false;
    if (isRunWideFailureCode(code)) return false;
    const history: any[] = Array.isArray(job?.errorHistory) ? job.errorHistory : [];
    const same = history.filter((entry) => (
        String(entry?.code || '').trim().toUpperCase() === code
        && !AUTOMATIC_WAIT.test(String(entry?.message || ''))
    ));
    return same.length >= 2;
}

function hasActionablePromotionProof(project: any, projectId: string) {
    const backendDisabled = (import.meta as any).env?.VITE_USE_BACKEND === 'false'
        || (import.meta as any).env?.VITE_USE_BACKEND === false;
    return hasDurableIntakeReadyProof(project, projectId, { backendDisabled });
}

function normalisePath(value: unknown) {
    return String(value || '')
        .replace(/\\/g, '/')
        .replace(/^\.\//, '')
        .replace(/\/{2,}/g, '/')
        .replace(/^\/+|\/+$/g, '')
        .trim();
}

function isUuidLike(value: unknown) {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
        .test(String(value || '').trim());
}

function cleanString(value: unknown) {
    return typeof value === 'string' ? value.trim() : '';
}

function firstNonBlank(...values: unknown[]) {
    for (const value of values) {
        const cleaned = cleanString(value);
        if (cleaned) return cleaned;
    }
    return '';
}

function isHumanProjectName(value: unknown) {
    const candidate = String(value || '').trim();
    return !!candidate
        && !isUuidLike(candidate)
        && !['queued project', 'untitled project', 'project details loading…']
            .includes(candidate.toLowerCase());
}

function firstPathSegment(file: any) {
    const path = normalisePath(file?.relativePath || file?.path || file?.name);
    const slash = path.indexOf('/');
    return (slash === -1 ? '' : path.slice(0, slash)).trim();
}

function intakeRoleForFile(file: any) {
    return matchProjectSubfolder(firstPathSegment(file))?.role || null;
}

function isImportableFile(file: any) {
    return !!file?.id && file?.kind !== 'unsupported' && file?.readable !== false;
}

// QuoteMaker's OWN outputs must never round-trip into intake. A quoted flat
// folder gains a "03. Quote" subfolder (the generated PDF) and possibly a
// "99. QM" working folder; feeding those back into the next run would
// contaminate the draft with the previous quote's prices.
//
// 2026-07-29 CORRECTION: an earlier version of this also excluded bare
// `photos/`, `documents/` and `videos/` as "legacy sync mirrors". That was
// wrong and dangerous — those exact names are OPERATOR evidence folders in
// this codebase (`EVIDENCE_DIRECTORY_PATTERNS`, file-explorer-intake.ts:83-95,
// which is what makes a folder count as a project at all, :262). Excluding
// them silently dropped every site photo of any operator folder shaped
// `Job/RFQ.pdf + Photos/*.jpg` — precisely the silent exclusion this wave
// exists to eliminate. Only the unambiguous QuoteMaker-authored surfaces are
// excluded: the numbered non-intake convention folders and `99. QM`.
function isQmGeneratedSegment(segment: string) {
    const trimmed = String(segment || '').trim();
    if (!trimmed) return false;
    if (/^99[.\s_-]*qm$/i.test(trimmed)) return true;
    const spec = matchProjectSubfolder(trimmed);
    return !!spec && spec.kind !== 'intake';
}

// Root-level files QuoteMaker itself writes into the project folder. The
// scanner already drops QuoteData.json / SyncManifest.json / project_index.md
// / ai_learnings.json / risk_report.txt; Inspector_Notes.txt is written by
// sync-folder too but was missing from that list, so it rode into intake as
// an ordinary .txt.
const QM_GENERATED_ROOT_FILES = new Set(['inspector_notes.txt']);

function isQmGeneratedFile(file: any) {
    const path = normalisePath(file?.relativePath || file?.path || file?.name);
    const leaf = path.includes('/') ? path.slice(path.lastIndexOf('/') + 1) : path;
    return QM_GENERATED_ROOT_FILES.has(leaf.toLowerCase());
}

export function isIntakeEligibleFile(file: any) {
    return isImportableFile(file)
        && !isQmGeneratedSegment(firstPathSegment(file))
        && !isQmGeneratedFile(file);
}

/** Ids of every file the current scan would allow into intake for a project. */
export function getIntakeEligibleFileIds(project: any): Set<string> {
    return new Set(
        (Array.isArray(project?.files) ? project.files : [])
            .filter(isIntakeEligibleFile)
            .map((file: any) => file.id),
    );
}

/**
 * Return the supported descendants of the two project-level packages the RJ
 * Bird workflow uses. Folder matching delegates to the shared Customer Zero
 * convention so the import gate agrees with the scanner: harmless operator
 * variants such as `1 RFQ Package` and `02 Site Documentation Package` are
 * accepted, while unrelated or differently numbered folders remain excluded.
 */
export function getRequiredPackageSelection(files: any[] = [], selectedIds: Iterable<string> = []) {
    const selected = selectedIds instanceof Set ? selectedIds : new Set(selectedIds || []);
    const packages: Record<string, any> = {};
    const recommendedFiles: any[] = [];

    for (const definition of REQUIRED_INTAKE_PACKAGES) {
        const packageFiles = (Array.isArray(files) ? files : []).filter((file) => (
            isImportableFile(file)
            && intakeRoleForFile(file) === definition.role
        ));
        const selectedFiles = packageFiles.filter((file) => selected.has(file.id));
        packages[definition.key] = {
            ...definition,
            files: packageFiles,
            fileIds: packageFiles.map((file) => file.id),
            count: packageFiles.length,
            selectedCount: selectedFiles.length,
            present: packageFiles.length > 0,
            fullySelected: packageFiles.length > 0 && selectedFiles.length === packageFiles.length,
        };
        recommendedFiles.push(...packageFiles);
    }

    const missing = REQUIRED_INTAKE_PACKAGES
        .filter((definition) => !packages[definition.key]?.present)
        .map((definition) => definition.folderName);
    const incomplete = REQUIRED_INTAKE_PACKAGES
        .filter((definition) => packages[definition.key]?.present && !packages[definition.key]?.fullySelected)
        .map((definition) => definition.folderName);
    const recommendedSet = new Set(recommendedFiles.map((file) => file.id));
    const extraSelectedIds = Array.from(selected).filter((id) => !recommendedSet.has(id));

    return {
        packages,
        recommendedFiles,
        recommendedFileIds: recommendedFiles.map((file) => file.id),
        hasRequiredPair: missing.length === 0,
        selectionComplete: missing.length === 0 && incomplete.length === 0,
        exactSelection: missing.length === 0 && incomplete.length === 0 && extraSelectedIds.length === 0,
        extraSelectedIds,
        missing,
        incomplete,
    };
}

export function getProjectRecommendedSelection(project: any) {
    return getRequiredPackageSelection(project?.files || [], []);
}

/**
 * Every queue entry point uses this project scope.
 *
 * 2026-07-29 (operator directive, batch-quoting-first-run): the Folder 01 + 02
 * convention is a PREFERENCE, not a gate. Customer Zero's real archive is flat
 * folders — photos, videos, PDFs and emails sitting directly in the project
 * folder — and the v5.47.0 fail-closed pair requirement made every start
 * button on the Import Projects surface permanently disabled ("looks fancy
 * but doesn't work"). The rule now:
 *
 *   - Both packages present  → queue exactly their readable descendants
 *     (the curated intake, unchanged).
 *   - Otherwise              → queue every supported readable file in the
 *     folder. Flat folders are first-class intake.
 *   - blocked only when there is literally nothing supported to import.
 *
 * `convention` tells the UI which rule applied so it can hint, not gate.
 */
export function getProjectQueueScope(project: any) {
    const selection = getProjectRecommendedSelection(project);
    if (selection.hasRequiredPair) {
        return {
            blocked: false,
            missing: [],
            selectedFiles: selection.recommendedFiles,
            selectedFileIds: selection.recommendedFileIds,
            selection,
            convention: 'packages' as const,
        };
    }
    const importableFiles = (Array.isArray(project?.files) ? project.files : [])
        .filter(isIntakeEligibleFile);
    if (importableFiles.length === 0) {
        return {
            blocked: true,
            missing: selection.missing,
            reason: 'no-supported-files' as const,
            selection,
            convention: 'all-files' as const,
        };
    }
    return {
        blocked: false,
        missing: [],
        selectedFiles: importableFiles,
        selectedFileIds: importableFiles.map((file: any) => file.id),
        selection,
        convention: 'all-files' as const,
    };
}

export function projectDisplayMeta(project: any) {
    const source = project || {};
    const formData = source?.formData || source?.quoteData?.formData || {};
    const persistedShape = buildQueueDisplayMeta({
        ...source,
        formData: { ...formData, ...(source?.formData || {}) },
    });
    return {
        name: persistedShape.name,
        address: persistedShape.address,
        quoteRef: persistedShape.quoteRef || extractQuoteReference(
            persistedShape.name,
            persistedShape.address,
            persistedShape.relativePath,
        ),
        relativePath: persistedShape.relativePath,
    };
}

/**
 * Resolve a queue row without ever making a UUID the visible project title.
 * New queue jobs may carry a persisted displayMeta snapshot; existing rows
 * are hydrated by QueueDrawer from Dexie and passed via projectMetaById.
 */
export function resolveQueueProjectIdentity(job: any, projectMetaById: Record<string, any> = {}) {
    const supplied = projectMetaById?.[job?.projectId] || {};
    const snapshot = job?.displayMeta || {};
    const legacySnapshot = job?.projectMeta || {};
    const suppliedPath = normalisePath(firstNonBlank(
        snapshot.relativePath,
        snapshot.path,
        legacySnapshot.relativePath,
        legacySnapshot.path,
        supplied.relativePath,
        supplied.syncRelativePath,
        supplied.path,
        supplied?.projectFolder?.relativePath,
        supplied?.projectFolder?.sourcePath,
    ));
    const pathName = suppliedPath.split('/').filter(Boolean).pop() || '';
    const name = [
        supplied.quoteTitle,
        supplied.title,
        supplied.name,
        supplied?.meta?.displayName,
        supplied?.formData?.projectName,
        supplied?.quoteData?.formData?.projectName,
        snapshot.name,
        snapshot.projectName,
        legacySnapshot.name,
        legacySnapshot.projectName,
        job?.projectName,
        pathName,
    ].map(cleanString).find(isHumanProjectName) || '';
    const address = firstNonBlank(
        supplied.address,
        supplied.siteAddress,
        supplied?.meta?.siteAddress,
        supplied?.formData?.address,
        supplied?.quoteData?.formData?.address,
        snapshot.address,
        snapshot.projectAddress,
        legacySnapshot.address,
        legacySnapshot.projectAddress,
        job?.projectAddress,
    );
    // Prefer the currently hydrated durable project. A historical displayMeta
    // snapshot is presentation evidence only and must not resurrect a stale QR
    // after the project/folder identity has been corrected.
    const durableQuoteRef = resolveProjectQuoteReference({
        ...supplied,
        id: supplied?.id || job?.projectId,
    }).quoteRef;
    const quoteRef = firstNonBlank(
        durableQuoteRef,
        snapshot.quoteRef,
        snapshot.quoteNumber,
        legacySnapshot.quoteRef,
        legacySnapshot.quoteNumber,
        job?.quoteRef,
        supplied.quoteRef,
        supplied.quoteNumber,
        supplied?.formData?.orderNumber,
        supplied?.quoteData?.formData?.orderNumber,
        extractQuoteReference(name, address, suppliedPath),
    );
    return {
        name: name || 'Project details loading…',
        address,
        quoteRef,
        hasHumanName: !!name,
    };
}

/**
 * Reconnect an unsynced File Explorer folder to the durable UUID queue row
 * created when it was materialised. The in-memory folderId -> UUID overlay is
 * lost when Import Projects closes, but both sides retain the same canonical
 * source path. Matching paths here keeps Queued/Processing/Failed/Needs review
 * truthful after reopening the importer without ever deduplicating by title.
 */
export function resolveDurableQueueAliases(projects: any[] = [], jobs: any[] = []) {
    const queueRows = Array.isArray(jobs) ? jobs : [];
    const deletedProjectIds = new Set(queueRows
        .filter(isProjectDeletionTombstone)
        .map((job) => String(job?.projectId || '').trim())
        .filter(Boolean));
    const replacementIdsByPredecessor = new Map<string, Set<string>>();
    for (const job of queueRows) {
        const recordsRestart = job?.reviewCode === 'RESTARTED_AS_NEW_ATTEMPT'
            || job?.review_code === 'RESTARTED_AS_NEW_ATTEMPT'
            || job?.completionOutcome === 'restarted-as-new-attempt'
            || job?.completion_outcome === 'restarted-as-new-attempt';
        if (!recordsRestart) continue;
        const predecessorId = String(job?.projectId || '').trim();
        const replacementId = String(
            job?.reviewDetails?.replacementProjectId
            ?? job?.review_details?.replacement_project_id
            ?? '',
        ).trim();
        if (!isUuidLike(predecessorId)
            || !isUuidLike(replacementId)
            || predecessorId.toLowerCase() === replacementId.toLowerCase()) continue;
        if (!replacementIdsByPredecessor.has(predecessorId)) {
            replacementIdsByPredecessor.set(predecessorId, new Set());
        }
        replacementIdsByPredecessor.get(predecessorId)!.add(replacementId);
    }

    // A clean restart deliberately leaves the cancelled predecessor in queue
    // history with the same source path. Follow its durable replacement edge
    // (including a later restart chain) so reopening Import Projects binds the
    // folder to the live attempt instead of treating audit history as a second,
    // ambiguous project. Conflicting/cyclic lineage fails closed.
    const latestRestartProjectId = (candidateId: string) => {
        let projectId = candidateId;
        const visited = new Set<string>();
        while (replacementIdsByPredecessor.has(projectId)) {
            if (visited.has(projectId)) return '';
            visited.add(projectId);
            const replacements = replacementIdsByPredecessor.get(projectId)!;
            if (replacements.size !== 1) return '';
            projectId = Array.from(replacements)[0] || '';
            if (!projectId) return '';
        }
        return deletedProjectIds.has(projectId) ? '' : projectId;
    };

    const projectIdsByPath = new Map<string, Set<string>>();
    for (const job of queueRows) {
        if (isProjectDeletionTombstone(job)) continue;
        const projectId = latestRestartProjectId(String(job?.projectId || '').trim());
        const relativePath = normalisePath(firstNonBlank(
            job?.displayMeta?.relativePath,
            job?.displayMeta?.path,
            job?.projectMeta?.relativePath,
            job?.projectMeta?.path,
        )).toLowerCase();
        if (!projectId || !relativePath) continue;
        // Ambiguous legacy queue history must fail closed instead of binding a
        // folder to an arbitrary job. A real source path should map to exactly
        // one durable project UUID.
        if (!projectIdsByPath.has(relativePath)) projectIdsByPath.set(relativePath, new Set());
        projectIdsByPath.get(relativePath)!.add(projectId);
    }

    const aliases = new Map<string, string>();
    for (const project of Array.isArray(projects) ? projects : []) {
        const folderId = String(project?.id || '').trim();
        if (!folderId || isUuidLike(folderId)) continue;
        const relativePath = normalisePath(firstNonBlank(
            project?.syncRelativePath,
            project?.relativePath,
            project?.path,
            project?.projectFolder?.relativePath,
            project?.projectFolder?.sourcePath,
        )).toLowerCase();
        const matchingProjectIds = relativePath ? projectIdsByPath.get(relativePath) : null;
        const durableProjectId = matchingProjectIds?.size === 1
            ? Array.from(matchingProjectIds)[0]
            : '';
        if (durableProjectId && isUuidLike(durableProjectId)) {
            aliases.set(folderId, durableProjectId);
        }
    }
    return aliases;
}

function newestJob(jobs: any[]) {
    return [...jobs].sort((a, b) => {
        const aTime = new Date(a?.completedAt || a?.startedAt || a?.enqueuedAt || 0).getTime() || 0;
        const bTime = new Date(b?.completedAt || b?.startedAt || b?.enqueuedAt || 0).getTime() || 0;
        if (aTime !== bTime) return bTime - aTime;
        return Number(b?.id || 0) - Number(a?.id || 0);
    })[0] || null;
}

export function deriveProjectWorkflowState(
    project: any,
    jobs: any[] = [],
    { promotionProofReadable = true }: { promotionProofReadable?: boolean } = {},
): ImportWorkflowState {
    const rows = Array.isArray(jobs) ? jobs.filter(job => job && !job.dismissedAt) : [];
    if (rows.some((job) => job.status === 'processing')) return 'processing';
    // A retry or downstream promotion supersedes older terminal audit rows.
    // Surface the work that is currently actionable rather than leaving a
    // project visually stuck on a historical failure/completion.
    if (rows.some((job) => job.status === 'pending')) return 'queued';
    // S2 (SPEC §3): staging states sit between queued and the historical
    // rows. A staged row beside an older terminal failure derives
    // staged/waiting_files — re-curation is never masked by history; the
    // card surfaces the prior failure as an inline banner instead
    // (collapseQueueJobs exposes it as `priorFailure`).
    if (rows.some((job) => job.status === 'waiting-files')) return 'waiting_files';
    if (rows.some((job) => job.status === 'staged')) return 'staged';

    // A completed intake row with no downstream prepare-quote lineage is an
    // actionable crash-recovery boundary. Keep it in Queued so Prepare all can
    // reconcile/promote it; labelling it Ready made the only recovery action
    // disappear because no pending row remained.
    const hasCompletedIntake = rows.some((job) => (
        (job.phase || 'prepare-quote') === 'pre-processing'
        && job.status === 'completed'
    ));
    const hasPrepareQuoteLineage = rows.some((job) => (
        (job.phase || 'prepare-quote') === 'prepare-quote'
    ));
    if (hasCompletedIntake && !hasPrepareQuoteLineage) {
        // The proof lives on the full local project row and nowhere else. A
        // caller holding only server summaries cannot see it, and ABSENT is
        // not the same as MISSING.
        //
        // Live, 2026-09-20: the QuoteMaker rail reads server summaries, whose
        // columns carry no backendVerified* fields and collapse the local
        // intake status to 'draft'. Every completed intake therefore derived
        // 'failed' there and was labelled "Stopped - unknown error" with no
        // error, while the Quote Run sheet, which reads Dexie, showed the same
        // projects correctly as "Intake complete". Twice during one run the
        // operator concluded the run had died.
        if (!promotionProofReadable) return 'queued';
        const projectId = String(rows.find((job) => job?.projectId)?.projectId || project?.id || '').trim();
        return hasActionablePromotionProof(project, projectId) ? 'queued' : 'failed';
    }

    const latestTerminal = newestJob(rows);
    if (latestTerminal?.status === 'failed') return 'failed';
    if ((latestTerminal?.phase || 'prepare-quote') === 'prepare-quote'
        && latestTerminal?.status === 'completed') {
        // S2 (SPEC §3): a reviewed draft the operator dismissed ("Clear
        // card", stamps clearedAt) derives ready — no tab, Browse only.
        // Uncleared completions stay needs_review.
        return latestTerminal?.clearedAt ? 'ready' : 'needs_review';
    }

    const status = String(project?.meta?.status || project?.status || '').toLowerCase();
    if (status === 'ready_for_builder_review' || status === 'quoted') return 'needs_review';
    if (status === 'failed' || status === 'error') return 'failed';
    return 'ready';
}

/** Collapse the two engine phases into one operator-facing project row. */
export function collapseQueueJobs(
    jobs: any[] = [],
    projectMetaById: Record<string, any> = {},
    { promotionProofReadable = true }: { promotionProofReadable?: boolean } = {},
) {
    const byProject = new Map<string, any[]>();
    for (const job of Array.isArray(jobs) ? jobs : []) {
        // Deletion tombstones stay in Dexie permanently so a retired UUID can
        // never be queued again, but they are audit-only and must not leave an
        // apparently uncleared project row in QueueDrawer/File Explorer.
        if (isProjectDeletionTombstone(job) || job?.dismissedAt) continue;
        const projectId = String(job?.projectId || '').trim();
        if (!projectId) continue;
        if (!byProject.has(projectId)) byProject.set(projectId, []);
        byProject.get(projectId)!.push(job);
    }

    return Array.from(byProject.entries()).map(([projectId, projectJobs]) => {
        const meta = projectMetaById?.[projectId] || {};
        const state = deriveProjectWorkflowState(meta, projectJobs, { promotionProofReadable });
        const preferred = state === 'processing'
            ? newestJob(projectJobs.filter((job) => job.status === 'processing'))
            : state === 'failed'
                ? newestJob(projectJobs.filter((job) => job.status === 'failed'))
                : state === 'needs_review'
                    ? newestJob(projectJobs.filter((job) => (job.phase || 'prepare-quote') === 'prepare-quote' && job.status === 'completed'))
                    : state === 'queued'
                        ? newestJob(projectJobs.filter((job) => job.status === 'pending'))
                        : state === 'staged' || state === 'waiting_files'
                            ? newestJob(projectJobs.filter((job) => job.status === 'staged' || job.status === 'waiting-files'))
                            : newestJob(projectJobs);
        const rawEffectiveJob = preferred || newestJob(projectJobs) || { projectId };
        // Phase promotion in older builds did not always copy displayMeta.
        // Preserve the current phase/status while borrowing only missing
        // identity fields from the newest sibling job for this same UUID.
        const siblingIdentitySources = [...projectJobs]
            .sort((a, b) => {
                const aTime = new Date(a?.completedAt || a?.startedAt || a?.enqueuedAt || 0).getTime() || 0;
                const bTime = new Date(b?.completedAt || b?.startedAt || b?.enqueuedAt || 0).getTime() || 0;
                if (aTime !== bTime) return bTime - aTime;
                return Number(b?.id || 0) - Number(a?.id || 0);
            })
            .flatMap((job) => [job?.displayMeta || {}, job?.projectMeta || {}]);
        const currentDisplayMeta = rawEffectiveJob?.displayMeta || rawEffectiveJob?.projectMeta || {};
        const mergedDisplayMeta = {
            ...currentDisplayMeta,
            name: [currentDisplayMeta, ...siblingIdentitySources]
                .map((source) => cleanString(source?.name || source?.title))
                .find(isHumanProjectName) || '',
            address: firstNonBlank(
                currentDisplayMeta.address,
                ...siblingIdentitySources.map((source) => source?.address),
            ),
            quoteRef: firstNonBlank(
                currentDisplayMeta.quoteRef,
                currentDisplayMeta.quoteNumber,
                ...siblingIdentitySources.flatMap((source) => [source?.quoteRef, source?.quoteNumber]),
            ),
            relativePath: firstNonBlank(
                currentDisplayMeta.relativePath,
                currentDisplayMeta.path,
                ...siblingIdentitySources.flatMap((source) => [source?.relativePath, source?.path]),
            ),
        };
        const effectiveJob = {
            ...rawEffectiveJob,
            displayMeta: siblingIdentitySources.some((source) => Object.keys(source).length > 0)
                || Object.keys(currentDisplayMeta).length > 0
                ? mergedDisplayMeta
                : null,
        };
        const hasCompletedIntake = projectJobs.some((job) => (
            (job.phase || 'prepare-quote') === 'pre-processing'
            && job.status === 'completed'
        ));
        const hasPrepareQuoteLineage = projectJobs.some((job) => (
            (job.phase || 'prepare-quote') === 'prepare-quote'
        ));
        const hasPromotionProof = promotionProofReadable && hasActionablePromotionProof(meta, projectId);
        const promotionReady = hasCompletedIntake && !hasPrepareQuoteLineage && hasPromotionProof;
        const promotionBlocked = promotionProofReadable
            && hasCompletedIntake && !hasPrepareQuoteLineage && !hasPromotionProof;
        // Files set aside (no text, no transcript, or unreadable) ride the
        // newest row that recorded them: intake, or a finished Quote Run, which
        // also names the files it set aside itself (2026-10-23). Surface them on
        // the collapsed project row so the drawer can SAY what was deferred
        // (DEC-BQ-030: never silent).
        const newestWithSetAside = newestJob(projectJobs.filter((job) => Array.isArray(job?.deferredMedia)));
        const deferredMedia = Array.isArray(newestWithSetAside?.deferredMedia)
            ? newestWithSetAside.deferredMedia
            : [];
        // S2 (SPEC §3 coexistence): a staged/waiting_files row outranks an
        // older terminal failure in the DERIVED state, but the failure must
        // not vanish — the run-card shows it as an inline banner ("Last run
        // failed at Stage N: {reason}"). Expose the newest failed sibling so
        // the surface never has to re-derive it.
        const priorFailure = (state === 'staged' || state === 'waiting_files')
            ? newestJob(projectJobs.filter((job) => job.status === 'failed'))
            : null;
        return {
            projectId,
            jobs: projectJobs,
            effectiveJob,
            state,
            priorFailure,
            promotionReady,
            promotionBlocked,
            deferredMedia,
            identity: resolveQueueProjectIdentity(effectiveJob, projectMetaById),
            position: (() => {
                const pendingPositions = projectJobs
                    .filter((job) => job.status === 'pending')
                    .map((job) => Number(job.position))
                    .filter(Number.isFinite);
                return pendingPositions.length > 0 ? Math.min(...pendingPositions) : null;
            })(),
        };
    }).sort((a, b) => {
        // 2026-07-29: needs_review used to sort LAST, below merely-queued and
        // even not-yet-queued rows. A finished draft waiting on the operator's
        // eyes is the entire point of running a batch — it belongs near the
        // top, under the two states that are actively wrong or in flight.
        const stateRank: Record<ImportWorkflowState, number> = {
            processing: 0,
            failed: 1,
            needs_review: 2,
            queued: 3,
            // S2: waiting sits above staged — it names an external blocker
            // (someone owes files) the operator may want to chase; staged is
            // parked by choice.
            waiting_files: 4,
            staged: 5,
            ready: 6,
        };
        const rankDiff = stateRank[a.state] - stateRank[b.state];
        if (rankDiff !== 0) return rankDiff;
        return (a.position || Number.MAX_SAFE_INTEGER) - (b.position || Number.MAX_SAFE_INTEGER);
    });
}
